/**
 * Command Center + ML — wizard state and everything derived from it.
 *
 * Every number comes from the server's `engine` block: history facts, the
 * GeoLift designs (synthetic control) or the Adrsta RCT engine's design (RCT),
 * and the mid-flight reads (readouts taken while the ads run). Designs and reads run as background jobs;
 * `live` (from the API) carries their progress while they run.
 */

import { CcTest, Engine, RegressionType } from "./api";
import { CardInfo, CheckSummary, DECISION_LABEL, FLOW_V, Tone, addDays, fmtDay, money, onTrack, parseDay, quarterOf } from "./common";

export interface MlState {
  name: string;
  adv: string;
  kpi: "rev" | "cnt";
  method: "sc" | "rct";
  channel: string;
  /** The question this test answers, typed by the user (older tests hold a preset key). */
  decision: string;
  startDate: string;
  maxWeeks: number;
  /** Max weekly spend in test markets, in $K. */
  budget: number;
  change: "no" | "yes";
  /** What else changes in the test markets, when change is "yes". */
  changeNote?: string;
  anchors?: string[];
  /** Synthetic control: markets where ads will run (pipeline keys); unset = the suggestions. */
  scMarkets?: string[];
  /** Synthetic control: campaign start date in the history (GeoLift's "Campaign Start Date"); unset = last maxWeeks. */
  scCampaignStart?: string;
  /** Synthetic control: GeoLift's "Regression Type"; unset = Ridge ASCM. */
  scRegressionType?: RegressionType;
  /** Synthetic control: GeoLift's "Statistical Significance Threshold %" (50-100); unset = 80. */
  scStatSig?: number;
  designId: number | string | null;
  split: 50 | 60 | 70 | 80;
  /** RCT: business-as-usual weekly ad spend across all markets, in $K (entered monthly; the engine sizes investment from it). */
  rctBauK?: number;
  /** Average monthly BAU media spend across all markets, in $K, as typed (GeoLift's "Average Monthly BAU Media Spent"); used by both methods. */
  rctBauMonthlyK?: number;
  /** RCT Setup inputs, as in the RCT Experiment: candidate durations (weeks, in the order typed),
   * weekly incremental spend cap ($) and total weekly BAU ad spend ($). */
  rctDurations?: number[];
  rctWeeklyCap?: number;
  rctWeeklySpend?: number;
  bar: number;
  cntBar: number;
  onWin: "scale" | "roll" | "keep";
  phase: number;
  finalDecision: string;
  saved: boolean;
  stage: number;
  maxStage: number;
  flightWeeks?: number;
  /** Stage-numbering version (common.migrateFlow). */
  flowV?: number;
  result?: { lift: number; roas: number | null; met: boolean; weeks: number; source: string; channel: string; season: string };
}

export const ML_DEFAULT: MlState = {
  name: "", adv: "", kpi: "rev", method: "sc", channel: "CTV", decision: "", startDate: "", maxWeeks: 6, budget: 60,
  change: "no", designId: null, split: 50, bar: 2.0, cntBar: 4, onWin: "scale", phase: 0,
  finalDecision: "", saved: false, stage: 0, maxStage: 0, flowV: FLOW_V,
};

/** Measurement is mid-flight (In flight), so there is no separate Measure stage. Phases: 0 scheduled, 1 running, 2 ended. */
export const ML_STAGES = ["Plan", "Design", "Test plan", "In flight", "Decide"];

export function computeMl(s: MlState, engine: Engine = {}) {
  const rev = s.kpi === "rev";
  const sc = s.method === "sc";
  const adv = s.adv.trim() || "the advertiser";
  const hist = engine.history?.status === "ready" ? engine.history : null;
  const facts = hist?.facts;
  const names = hist?.names;
  const power = engine.power || null;
  const designs = power?.status === "done" ? power.designs : [];
  // The plan covers every analysed market; older tests picked one row.
  const design = (power?.status === "done" ? power.plan : null) || designs.find(d => d.id === s.designId) || null;

  // RCT design from the Adrsta RCT engine
  const rct = engine.rct || null;
  const rctDesign = rct?.status === "done" ? rct.design || null : null;
  const split = rctDesign?.split ?? s.split;

  // Chosen design
  const weeks = sc ? (design?.weeks ?? s.maxWeeks) : (rctDesign?.weeks ?? s.maxWeeks);
  const mde = sc ? (design?.mde ?? 0) : (rctDesign?.mde ?? 0);
  const mdeText = mde.toFixed(1) + "%";
  // Synthetic control runs at the plan's test budget (GeoLift's investment is only an
  // estimate from BAU spend); the RCT engine sizes its design to that budget.
  const totalSpendK = sc ? (design ? s.budget * weeks : 0) : (rctDesign?.investment_k ?? 0);
  const wkSpendK = weeks ? totalSpendK / weeks : 0;
  const nMarkets = sc ? (design?.markets.length ?? 0) : (rctDesign?.test_markets ?? 0);
  const designDone = sc ? !!design : !!rctDesign;
  const start = parseDay(s.startDate);
  const end = start ? addDays(start, weeks * 7) : null;
  const flight = start && end ? `${fmtDay(start)} – ${fmtDay(end)} · ${weeks} wk` : `${weeks} wk`;

  // Readout (GeoLift for synthetic control, test-vs-control model for RCT); older readouts have no method
  const ro = engine.readout || null;
  const roMatches = (ro?.method ?? "sc") === s.method;
  let lift = 0, conf = 0, incremental = 0, iroas = 0, spendK = totalSpendK, costPer = 0, measured = false;
  if (ro?.status === "done" && roMatches) {
    measured = true;
    lift = ro.lift; conf = ro.confidence; incremental = ro.incremental; spendK = ro.spend_k;
    iroas = ro.iroas ?? 0; costPer = ro.cost_per_incremental ?? 0;
  }
  // Synthetic control: the threshold the power analysis ran with (GeoLift's "Statistical
  // Significance Threshold %"); RCT keeps 80%.
  const sigThreshold = sc ? (power?.stat_sig_threshold ?? 80) : 80;
  const sig = measured && conf >= sigThreshold;
  const met = sig && (rev ? iroas >= s.bar : lift >= s.cntBar);
  // Mid-flight reads (oldest first) for this method; a read is final once it covers the whole flight.
  const reads = (engine.reads || []).filter(r => (r.method ?? "sc") === s.method);
  const final = measured && ro?.final !== false;
  const weeksIn = ro?.weeks_in ?? weeks;
  const tracking = measured && onTrack(rev, iroas, lift, s.bar, s.cntBar);

  const methodShort = sc ? "Synthetic control" : `RCT ${split}/${100 - split}`;
  const barCond = rev ? `iROAS ≥ ${s.bar.toFixed(1)}x and significant` : `lift ≥ ${s.cntBar}% and significant`;

  return {
    rev, sc, adv, hist, facts, names, power, designs, design,
    rct, rctDesign, split,
    weeks, mde, mdeText, totalSpendK, wkSpendK, nMarkets, designDone, start, end, flight, reads, final, weeksIn, tracking,
    ro: roMatches ? ro : null, measured, lift, conf, incremental, iroas, spendK, costPer, sig, sigThreshold, met,
    methodShort, barCond,
    kpiShort: rev ? "KPI: revenue" : "KPI: count",
    liftText: (lift >= 0 ? "+" : "") + lift.toFixed(1) + "%",
    effLabel: rev ? "iROAS" : "Cost per incremental",
    effText: rev ? iroas.toFixed(2) + "x" : "$" + (costPer || 0).toFixed(2),
    curve: design ? power?.curves?.[String(design.id)] || [] : [],
  };
}

export type MlDerived = ReturnType<typeof computeMl>;

export function mlStatus(s: MlState): { key: string; label: string; tone: Tone } {
  switch (s.maxStage) {
    case 0: return { key: "draft", label: "Draft", tone: "violet" };
    case 1: return { key: "planning", label: "Planning", tone: "violet" };
    case 2: return { key: "designing", label: "Designing", tone: "violet" };
    case 3: return { key: "plan_ready", label: "Plan ready", tone: "blue" };
    case 4:
      if (s.phase >= 2) return { key: "ended", label: "Flight ended", tone: "violet" };
      if (s.phase >= 1) return { key: "in_flight", label: "Running", tone: "amber" };
      return { key: "scheduled", label: "Scheduled", tone: "amber" };
    default: return s.saved ? { key: "closed", label: "Closed", tone: "green" } : { key: "deciding", label: "Deciding", tone: "green" };
  }
}

export function mlCard(s: MlState, engine: Engine, checks?: CheckSummary, live: CcTest["live"] = {}): CardInfo {
  const d = computeMl(s, engine);
  const st = mlStatus(s);
  const stageN = Math.max(1, s.maxStage);
  const chips = [`${stageN} · ${ML_STAGES[stageN - 1]}`, d.methodShort, `${fmtDay(d.start)} · ${d.weeks} wk`];
  const base = { segDone: Math.max(0, stageN - 1), segCur: true, chips };

  if (s.saved) {
    // Older tests may carry a partial result (no lift); fall back to computed values.
    const r = typeof s.result?.lift === "number" ? s.result : undefined;
    return {
      ...base, badge: (r?.met ?? d.met) ? "Closed · bar met" : "Closed · bar missed", tone: (r?.met ?? d.met) ? "green" : "amber",
      bucket: "closed", segDone: 5, segCur: false, next: "",
      result: {
        lift: r ? `${r.lift >= 0 ? "+" : ""}${r.lift.toFixed(1)}%` : d.liftText,
        eff: r && r.roas != null ? `${r.roas.toFixed(1)}x` : d.effText, effLabel: d.effLabel,
        bar: d.rev ? s.bar.toFixed(1) + "x" : s.cntBar + "%", met: r?.met ?? d.met,
        decision: DECISION_LABEL[s.finalDecision] || "recorded",
      },
    };
  }
  if (checks && checks.block > 0) {
    return { ...base, badge: `${checks.block} blocker${checks.block > 1 ? "s" : ""}`, tone: "red", bucket: "action",
      next: checks.first_block ? `Blocker: ${checks.first_block}` : "Next: clear the blocker →", nextWarn: true };
  }
  const designJob = d.sc ? (d.power?.status === "running" ? live?.power : undefined) : (d.rct?.status === "running" ? live?.rct : undefined);
  const designRunning = d.sc ? d.power?.status === "running" : d.rct?.status === "running";
  const pct = (j?: { progress: number }) => (j && j.progress > 0 ? ` · ${j.progress}%` : "");
  switch (s.maxStage) {
    case 0:
    case 1: return { ...base, badge: st.label, tone: st.tone, bucket: "active", next: "Next: finish the plan →" };
    case 2: return { ...base, badge: designRunning ? `Design running${pct(designJob)}` : st.label, tone: st.tone, bucket: designRunning ? "active" : "action",
      next: designRunning ? (designJob?.message || (d.sc ? "Power analysis is running on the GeoLift engine…" : "The RCT engine is searching splits…"))
        : d.sc ? "Next: upload history and run the power analysis →" : "Next: upload history and run the RCT design →" };
    case 3: return { ...base, badge: st.label, tone: st.tone, bucket: "action", next: "Next: share the test plan and approve →" };
    case 4: {
      if (engine.readout?.status === "running") {
        return { ...base, badge: `Read running${pct(live?.readout)}`, tone: "violet", bucket: "active", next: live?.readout?.message || "A mid-flight read is running…" };
      }
      const latest = d.measured ? `latest read ${d.liftText} lift, week ${d.weeksIn} of ${d.weeks}` : "no read yet";
      if (s.phase >= 2) {
        return d.measured
          ? { ...base, badge: "Flight ended", tone: "violet", bucket: "action", next: d.final ? "Next: record the decision →" : `Next: upload data through the end date (${latest}) →`, nextWarn: !d.final }
          : { ...base, badge: "Flight ended", tone: "violet", bucket: "action", next: "Next: upload in-flight data and run a read →", nextWarn: true };
      }
      return { ...base, badge: s.phase >= 1 ? "In flight" : "Scheduled", tone: "amber", bucket: "active",
        next: s.phase >= 1 ? `Ends ${fmtDay(d.end)} · ${latest}${d.measured ? (d.tracking ? " (on track)" : " (behind the bar)") : ""}` : `Starts ${fmtDay(d.start)} · read mid-flight` };
    }
    default: return { ...base, badge: "Deciding", tone: "green", bucket: "action", next: "Next: record the decision →" };
  }
}

/** Library payload written into state when the test is closed. */
export function mlResult(s: MlState, engine: Engine): NonNullable<MlState["result"]> {
  const d = computeMl(s, engine);
  return {
    lift: d.lift, roas: d.rev ? Math.round(d.iroas * 10) / 10 : null, met: d.met, weeks: d.weeks,
    source: d.methodShort, channel: s.channel || "CTV", season: quarterOf(d.start),
  };
}

export { money };
